import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TenantMember } from '@/api/tenants/tenantsApi.types';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useAuth } from '@/hooks/useAuth';
import {
  useRemoveWorkspaceMemberMutation,
  useWorkspaceMembersQuery,
} from '@/hooks/useWorkspaceTeam';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

export function MembersTable() {
  const { t } = useTranslation('team');
  const { currentUser } = useAuth();
  const membersQuery = useWorkspaceMembersQuery();
  const removeMemberMutation = useRemoveWorkspaceMemberMutation();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [memberToRemove, setMemberToRemove] = useState<TenantMember | null>(null);

  const members = membersQuery.data === undefined ? [] : membersQuery.data;
  const normalizedSearch = debouncedSearch.trim().toLowerCase();
  const filteredMembers = members.filter(function matchesSearch(member) {
    if (normalizedSearch.length === 0) {
      return true;
    }
    return (
      member.fullName.toLowerCase().includes(normalizedSearch) ||
      member.email.toLowerCase().includes(normalizedSearch)
    );
  });
  const currentUserId = currentUser === null ? '' : currentUser.id;

  async function confirmRemoval() {
    if (memberToRemove === null) {
      return;
    }
    try {
      await removeMemberMutation.mutateAsync(memberToRemove.userId);
      showSuccessNotification(t('members.removed'));
      setMemberToRemove(null);
    } catch (removeError) {
      const backendMessage = resolveApiErrorMessage(removeError);
      showErrorNotification(backendMessage.length > 0 ? backendMessage : t('members.removeError'));
      setMemberToRemove(null);
    }
  }

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="UsersRound" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('members.heading')}</h2>
      </div>
      <div className="mb-3">
        <ListSearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('members.searchPlaceholder')}
        />
      </div>
      {membersQuery.isLoading === true ? (
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : membersQuery.isError === true ? (
        <QueryErrorState
          onRetry={function retry() {
            membersQuery.refetch();
          }}
        />
      ) : filteredMembers.length === 0 ? (
        <p className="text-[13px] text-zoomuted py-1">{t('members.empty')}</p>
      ) : (
        <div className="divide-y divide-zooborder">
          {filteredMembers.map(function renderMember(member) {
            return (
              <MemberRow
                key={member.userId}
                member={member}
                isSelf={member.userId === currentUserId}
                onRemove={function requestRemoval() {
                  setMemberToRemove(member);
                }}
              />
            );
          })}
        </div>
      )}

      {memberToRemove !== null ? (
        <ConfirmDialog
          title={t('members.removeConfirmTitle')}
          description={t('members.removeConfirmDescription', { name: memberToRemove.fullName })}
          confirmLabel={t('members.remove')}
          iconName="Trash"
          isConfirming={removeMemberMutation.isPending}
          onConfirm={function onConfirmRemoval() {
            void confirmRemoval();
          }}
          onCancel={function cancelRemoval() {
            setMemberToRemove(null);
          }}
        />
      ) : null}
    </Card>
  );
}

interface MemberRowProps {
  member: TenantMember;
  isSelf: boolean;
  onRemove: () => void;
}

function MemberRow({ member, isSelf, onRemove }: MemberRowProps) {
  const { t } = useTranslation('team');
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-zooindigo truncate">{member.fullName}</div>
        <div className="text-[11px] text-zoomuted truncate">{member.email}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {member.isOwner === true ? (
          <Badge tone="accent" size="sm">
            {t('members.owner')}
          </Badge>
        ) : (
          <Badge tone="muted" size="sm">
            {t('members.member')}
          </Badge>
        )}
        {member.isOwner === true || isSelf === true ? null : (
          <Button
            variant="destructiveGhost"
            size="icon"
            aria-label={t('members.remove')}
            title={t('members.remove')}
            onClick={onRemove}
          >
            <Icon name="Trash" />
          </Button>
        )}
      </div>
    </div>
  );
}
